import type React from 'react';
import { createTheme } from '@mui/material/styles';
import type { TravelMode, StopKind } from '../api/types';

/**
 * Light and dark are both defined up front and switched by CSS variables, so
 * the map and the surrounding chrome always agree on which mode they are in.
 */
export const theme = createTheme({
  cssVariables: { colorSchemeSelector: 'data-mui-color-scheme' },
  colorSchemes: {
    light: {
      palette: {
        primary: { main: '#1d6f6a' },
        secondary: { main: '#b4562a' },
        background: { default: '#f6f7f5', paper: '#ffffff' },
      },
    },
    dark: {
      palette: {
        primary: { main: '#5bbdb4' },
        secondary: { main: '#e08a5a' },
        background: { default: '#101614', paper: '#18211f' },
      },
    },
  },
  shape: { borderRadius: 10 },
  typography: {
    fontFamily: '"Inter", "Segoe UI", system-ui, -apple-system, sans-serif',
    h1: { fontSize: '1.7rem', fontWeight: 650, letterSpacing: '-0.02em' },
    h2: { fontSize: '1.3rem', fontWeight: 620, letterSpacing: '-0.01em' },
    h3: { fontSize: '1.05rem', fontWeight: 600 },
    button: { textTransform: 'none', fontWeight: 560 },
  },
  components: {
    MuiButton: { defaultProps: { disableElevation: true } },
    MuiPaper: { defaultProps: { elevation: 0 }, styleOverrides: { root: { backgroundImage: 'none' } } },
    MuiTextField: {
      defaultProps: {
        size: 'small',
        /*
          A number field must not be edited by scrolling past it.

          A focused `<input type="number">` takes the mouse wheel as up and
          down. Scroll the panel with the pointer over one and the value
          changes under the cursor: a keep-clear distance set to 200 m arrived
          at 50 m after three notches, silently, while somebody was only
          trying to reach the controls below it.

          Blurring on wheel hands the gesture back to the page — the field
          loses focus, the panel scrolls, the number stays. Set here because
          the app has twenty of these across thirteen files and the next one
          written would have had the same hole.
        */
        onWheel: (event: React.WheelEvent<HTMLDivElement>) => {
          // `instanceof` narrows the EventTarget on its own, so no cast.
          const el = event.target;
          if (el instanceof HTMLInputElement && el.type === 'number') el.blur();
        },
      },
    },
    MuiSelect: { defaultProps: { size: 'small' } },
  },
});

/**
 * Route colours by travel mode. Chosen to stay distinguishable against both
 * light and dark basemaps, and from the amber used for avoidance zones.
 */
export const MODE_COLOURS: Record<TravelMode, string> = {
  walk: '#2f8f86',
  bike: '#3a7bd5',
  motorcycle: '#8e5bc4',
  car: '#c2562c',
  taxi: '#d9a520',
  truck: '#8a6d3b',
  transit: '#2d6ca8',
};

/**
 * The way home on a round trip.
 *
 * A loop draws its outward and return legs over the same streets, so one
 * colour makes the two indistinguishable. Deliberately a different hue rather
 * than a lighter shade of the mode colour, which reads as "less important"
 * instead of "the other direction".
 */
export const RETURN_LEG_COLOUR = '#2d6ca8';

export const STOP_COLOURS: Record<StopKind, string> = {
  start: '#2e7d32',
  end: '#c62828',
  visit: '#1d6f6a',
  lunch: '#e07a2f',
  coffee: '#8d6e63',
};

/**
 * Ground somebody has already planned.
 *
 * Deliberately not a shade of the visit teal: "has been worked before" is a
 * different fact about a place from "this is a stop", and a paler teal would
 * read as a fainter version of the same thing. Violet is used nowhere else
 * on a map, so a catalogue of rings sorts into two kinds at a glance.
 */
export const COVERED_COLOUR = '#7b4fb5';

/** Avoidance zones are drawn in a warning colour, never a route colour. */
export const AVOID_COLOUR = '#d4791f';
export const AVOID_VIOLATED_COLOUR = '#c62828';

/**
 * POI dot colours, by category group.
 *
 * Grouped rather than per-category so the map stays readable with a dozen
 * categories switched on: a traveller needs to tell safety infrastructure
 * from somewhere to eat at a glance, not distinguish museums from galleries.
 */
export const GROUP_COLOURS: Record<string, string> = {
  safety: '#c62828',
  civic: '#6d4c9f',
  attraction: '#1d6f6a',
  food: '#e07a2f',
  shopping: '#b4562a',
  lodging: '#7b5e57',
  transport: '#4a6b7c',
  other: '#78716c',
};

export const DEFAULT_GROUP_COLOUR = GROUP_COLOURS.other;

/** MapLibre expression colouring each POI dot by its group property. */
export const POI_COLOUR_EXPRESSION = [
  'match',
  ['get', 'group'],
  ...Object.entries(GROUP_COLOURS).flatMap(([group, colour]) => [group, colour]),
  DEFAULT_GROUP_COLOUR,
];

/**
 * Twenty-four colours chosen to stay distinguishable from each other and
 * from the basemap. Hashing straight to a hue clustered similar keys — park,
 * police and hospital all landed within 20 degrees — so categories pick from
 * a curated set instead.
 */
const CATEGORY_PALETTE = [
  '#1f77b4', '#d62728', '#2ca02c', '#ff7f0e', '#9467bd', '#8c564b',
  '#e377c2', '#17becf', '#bcbd22', '#7f3f98', '#c2185b', '#00796b',
  '#5d4037', '#455a64', '#f57c00', '#0288d1', '#388e3c', '#ab47bc',
  '#d81b60', '#00838f', '#6d4c41', '#3949ab', '#7cb342', '#e64a19',
];

/**
 * Assigns each category a colour by its position in the sorted list.
 *
 * Hashing the key to a palette index collided badly — museum, gallery and
 * hospital all landed on the same orange. Positional assignment guarantees
 * the first 24 categories are all different, and stays stable because the
 * ordering is alphabetical rather than dependent on what is selected.
 *
 * Beyond 24 the palette repeats, but repeats fall far apart alphabetically
 * and only selected categories are ever drawn together.
 */
export function buildCategoryColours(keys: string[]): Map<string, string> {
  const sorted = [...new Set(keys)].sort();
  return new Map(
    sorted.map((key, index) => [key, CATEGORY_PALETTE[index % CATEGORY_PALETTE.length]!]),
  );
}

/** Icon glyph per category group, drawn onto the map markers. */
export const GROUP_GLYPHS: Record<string, string> = {
  safety: '+',
  civic: '⚑',
  attraction: '★',
  food: '◉',
  shopping: '■',
  lodging: '▲',
  transport: '●',
  other: '●',
};

/**
 * A colour per day of a multi-day operation, distinct enough to tell four
 * routes apart on one map.
 *
 * Not the category palette: these identify a DAY, not a kind of place, and
 * borrowing the category colours would have Tuesday looking like a museum.
 *
 * Here rather than on the planner page because the Saved screen draws the
 * same days, and a day that is teal on one screen and plum on the other is
 * two answers to one question.
 */
export const DAY_COLOURS = [
  '#1d6f6a', '#b4531f', '#4a5aa8', '#7a4a86',
  '#2f7d32', '#a03050', '#8a6d1f', '#396a8a',
  '#6a4a2a', '#3f7a72', '#8a3a6a', '#55703a',
  '#7a3030', '#3a5a7a',
];

export const colourForDay = (index: number) =>
  DAY_COLOURS[index % DAY_COLOURS.length]!;
